import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Alarm, AlarmLevel, AlarmList, ComponentDefinition, Dictionary, IValueViewModel, Identifier, SelectorType, WidgetValues } from '../../models';
import { IDashboardLoadService, IDateRangeService, IExportSvgService, IExportToCsvService } from '../../services/';
import { momentFormat, infoTextColor } from '../../common/constants';
import { IWidgetConfiguration, SeriesConfiguration, IWidgetFactory, IWidget } from '../../widgets';
import { ChartConfigurationOption } from '../../wtFramework';
import { IAlarmHelper } from '../../common/helpers';
import { refreshClick } from '../../common/events';

class IdentifierAlarmsTresholdsController implements angular.IComponentController {
    alarmList: AlarmList = {};
    alarms: Alarm[];
    data: WidgetValues;
    exposedScope: any;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    trend: IWidget;

    readonly identifier: Identifier;
    readonly selectedAlarm: Alarm;
    readonly viewModels: Dictionary<IValueViewModel>;

    private _garbages: (() => void)[] = [];

    static $inject = ['$scope', 'dateRangeService', 'widgetFactory', 'exportSvgService', 'exportToCsvService', 'dashboardLoadService', 'alarmHelper'];

    constructor(
        private _$scope: angular.IScope,
        private _dateRangeService: IDateRangeService,
        private _widgetFactory: IWidgetFactory,
        private _exportSvgService: IExportSvgService,
        private _exportCsvService: IExportToCsvService,
        private _dashboardLoadService: IDashboardLoadService,
        private _alarmHelper: IAlarmHelper
    ) {}

    $onInit() {
        this.trend = this.createTrend();

        this._garbages.push(
            this._$scope.$watch(
                () => this.alarms,
                () => {
                    this.refreshAlarms();
                },
                true
            )
        );

        this._garbages.push(
            this._$scope.$watch(
                () => this.selectedAlarm,
                () => {
                    this.refreshSerie().then(() => this.refreshAlarms());
                },
                true
            )
        );

        this._garbages.push(
            this._$scope.$watch(
                () => this.identifier,
                () => {
                    this.refreshSerie().then(() => this.refreshAlarms());
                },
                true
            )
        );

        this.exposedScope = {
            exportToCsv: this.exportToCsv.bind(this),
            exportToPng: this.exportToPng.bind(this),
            exportToSvg: this.exportToSvg.bind(this)
        };
        this.data = { entries: [] } as any;

        this._garbages.push(
            this._$scope.$on(refreshClick, (event, data) => {
                if (this.identifier) {
                    this.refresh(data.beginDate, data.endDate, data.force).then(() => {
                        if (this.selectedAlarm) {
                            this.refreshAlarms();
                        }
                    });
                }
            })
        );
    }

    $onDestroy() {
        this._garbages.forEach((g) => g());
    }

    private refresh(beginDate: string, endDate: string, force: boolean) {
        this.loadingMessage = 'Loading data';
        this.isLoading = true;
        return this._dashboardLoadService
            .refresh([this.trend], beginDate, endDate, null, null, null, force)
            .then((data) => {
                // TODO display warning if data for trend widget not found?
                this.data = _.find(data, (d) => d.guid === this.trend.guid);
            })
            .finally(() => (this.isLoading = false));
    }

    private refreshAlarms() {
        if (this.identifier) {
            if (this.alarms && this.alarms.length > 0) {
                const alarmList = {};
                alarmList[this.identifier.guid] = this.alarms;
                this.alarmList = alarmList;
            } else {
                this.alarmList = {};
            }
        }
        this.refreshMinMax();
    }

    private createTrend() {
        const config: IWidgetConfiguration = { sizeX: 3, sizeY: 3, col: 3, row: 0 };
        const trend = this._widgetFactory.createWidget('tagsTrend', config); // set a :type to trend
        (trend.configuration as any).showAlarms = true;
        const options = new ChartConfigurationOption();

        options.isTitleVisible = false;
        options.isLegendVisible = false;
        options.isHorizontalGridlinesVisible = true;

        trend.configuration.isEndDateIncluded = true;
        trend.configuration.options = options;
        trend.configuration.options.panes = [
            {
                height: 1,
                isXaxisVisible: true,
                isYaxisVisible: true,
                isHorizontalGridLinesVisible: true
            }
        ];
        trend.configuration.actionItems = [{ icon: 'fa fa-cog fa-fw' }];

        return trend;
    }

    private refreshSerie(): angular.IPromise<void> {
        this.trend.configuration.series = this.createSerie();
        return this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string, false);
    }

    private refreshMinMax() {
        if (this.alarms && this.identifier && this.selectedAlarm) {
            const lines = this._alarmHelper.getMinMaxLines(this.alarms, this.selectedAlarm, false);
            this.getMainPane().horizontalLines = lines;
        }
    }

    private createSerie(): SeriesConfiguration[] {
        if (!this.identifier) {
            return [];
        }

        const value = this.selectedAlarm ? this.selectedAlarm.value : 'average';
        const serie = new SeriesConfiguration(this.identifier.guid, infoTextColor, 'circle');

        serie.valueType = value;
        serie.value = value;
        serie.type = SelectorType.identifier;
        serie.isLineVisible = true;
        serie.isSymbolVisible = false;

        return [serie];
    }

    private getMainPane() {
        return this.trend.configuration.options.panes[0];
    }

    exportToPng() {
        this.exportImage('PNG');
    }

    exportToSvg() {
        this.exportImage('SVG');
    }

    private exportImage(mode: 'SVG' | 'PNG') {
        const svg: SVGSVGElement = d3.select(`#id${this.trend.guid}`).select('svg').node() as any;
        this.isLoading = true;
        this.loadingMessage = `Exporting to ${mode}`;
        const fileName = `Alarms definition - ${this.identifier.name}`;
        const promise = mode === 'SVG' ? this._exportSvgService.exportSvg(fileName, svg) : this._exportSvgService.exportSvgAsPng(fileName, svg);
        return promise.finally(() => (this.isLoading = false));
    }

    exportToCsv() {
        this.loadingMessage = 'Exporting to csv';
        const vm = this.viewModels[this.identifier.guid];
        const data = this.data;
        const alarms = this.alarms;
        const result = this.generateCsvContent(data, alarms, vm);
        return this._exportCsvService.getCsvFromData(result, `Alarms definition - ${vm.name}.csv`);
    }

    private generateCsvContent(data: WidgetValues, alarms: Alarm[], vm: IValueViewModel): [string, string, string][] {
        const name = vm.name;
        const result: [string, string, string][] = [['TimeStamp', 'LocalTimeStamp', name]];
        result.push(['Unit', '', vm.unitName]);
        result.push(['Short comment', '', vm.shortComment]);
        result.push(['Values', '', 'average']);
        alarms.forEach((a) => {
            if ((a.low || a.low === 0) && !a.isManual) {
                result.push([AlarmLevel[a.level] + ' low', '', a.low.toString()]);
            }
            if ((a.high || a.high === 0) && !a.isManual) {
                result.push([AlarmLevel[a.level] + ' high', '', a.high.toString()]);
            }
        });
        data.values.forEach((d) => {
            result.push([
                moment.utc(d.timeStamp).format(momentFormat),
                moment(d.timeStamp).format(momentFormat),
                d.value || d.value === 0 ? d.value.toString() : ''
            ]);
        });
        return result;
    }
}

export const wtIdentifierAlarmsTresholds: ComponentDefinition = {
    componentId: 'wtIdentifierAlarmsTresholds',
    controller: IdentifierAlarmsTresholdsController,
    template: require('./identifierAlarmsTresholds.component.html'),
    controllerAs: 'vm',
    bindings: {
        identifier: '<',
        selectedAlarm: '<',
        alarms: '=',
        isEditMode: '=',
        trend: '=',
        exposedScope: '=?',
        viewModels: '<'
    }
};
